<template>
  <div class="hunt-areas">
    <Table
      :headerConfigs="tableHeaderConfigs"
      :tableData="huntAreas"
      initialSortProperty="name"
      :initialSortAscending="true"
      v-if="hasHuntAreas"
    >
      <template v-slot:tableRow="{ rowObject: huntArea }">
        <td>
          <span class="tag" :class="statusTagClass(huntArea)">{{
            statusText(huntArea)
          }}</span>
        </td>
        <td>
          <span>{{ huntArea.name }}</span>
        </td>
        <td>
          <Spinner
            width="20"
            height="20"
            v-if="isLoadingHuntAreaInfo(huntArea)"
          />
          <span class="has-text-danger" v-if="huntAreaInfoLoadFailed(huntArea)"
            >[failed]</span
          >
          <span
            :class="hasBorderTextClass(huntArea)"
            v-if="huntAreaInfo(huntArea)"
          >
            {{ huntAreaInfo(huntArea).hasBorder ? "YES" : "NO" }}
          </span>
        </td>
        <td>
          <Spinner
            width="20"
            height="20"
            v-if="isLoadingHuntAreaInfo(huntArea)"
          />
          <span class="has-text-danger" v-if="huntAreaInfoLoadFailed(huntArea)"
            >[failed]</span
          >
          <span v-if="huntAreaInfo(huntArea)">
            {{ huntAreaInfo(huntArea).markersCount }}
          </span>
        </td>
        <td>
          <a @click="huntAreaSelected(huntArea)">{{ huntArea.id }}_LLGroup</a>
        </td>
        <td>
          <span v-if="huntArea.createdBy">
            <span v-if="nameOfUserWithId(huntArea.createdBy)"
              >{{ nameOfUserWithId(huntArea.createdBy) }} (<span
                >{{ huntArea.createdBy }}_LLUser</span
              >)</span
            >
            <span v-else>{{ huntArea.createdBy }}_LLUser</span>
          </span>
        </td>
        <td>
          <span>{{ huntArea.county }}</span>
        </td>
      </template>
    </Table>
  </div>
</template>

<script>
import Spinner from "@/components/utils/Spinner";
import Table from "@/components/utils/Table";

import {
  LOAD_USERS_WITH_IDS,
  GET_USER_WITH_ID
} from "@/modules/users/usersCacheStore.js";

import {
  LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS,
  IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID,
  GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID,
  GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID
} from "./huntAreasCacheStore";

export default {
  name: "HuntAreasList",

  components: { Spinner, Table },

  props: {
    memberUserId: String,

    huntAreas: {
      type: Array,
      default: () => []
    },

    usersCacheStorePath: {
      type: String,
      required: true
    },

    huntAreasCacheStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      tableHeaderConfigs: [
        {
          property: null,
          title: "",
          sortable: false
        },
        {
          property: "name",
          title: "Name",
          sortable: true
        },
        {
          property: null,
          title: "Has Border",
          sortable: false
        },
        {
          property: null,
          title: "PoIs Count",
          sortable: false
        },
        {
          property: "id",
          title: "Id",
          sortable: true
        },
        {
          property: "createdBy",
          title: "Created By",
          sortable: true
        },
        {
          property: "county",
          title: "County",
          sortable: true
        }
      ]
    };
  },

  mounted() {
    const loadUserWithIdsAction = `${this.usersCacheStorePath}/${LOAD_USERS_WITH_IDS}`;
    const userIds = this.huntAreas.map(huntArea => huntArea.createdBy);
    this.$store.dispatch(loadUserWithIdsAction, { userIds });

    const loadHuntAreasInfoAction = `${this.huntAreasCacheStorePath}/${LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS}`;
    const huntAreaIds = this.huntAreas.map(huntArea => huntArea.id);
    this.$store.dispatch(loadHuntAreasInfoAction, { huntAreaIds });
  },

  methods: {
    statusText(huntArea) {
      return this.isAdmin(huntArea) ? "admin" : "member";
    },

    statusTagClass(huntArea) {
      const isAdmin = this.isAdmin(huntArea);

      return {
        "is-light": !isAdmin,
        "is-success": isAdmin
      };
    },

    isAdmin(huntArea) {
      return huntArea.createdBy === this.memberUserId;
    },

    huntAreaSelected(huntArea) {
      this.$emit("hunt-area-selected", huntArea);
    },

    nameOfUserWithId(userId) {
      if (!userId) {
        return null;
      }

      const getUserWithIdPath = `${this.usersCacheStorePath}/${GET_USER_WITH_ID}`;
      const user = this.$store.getters[getUserWithIdPath](userId);

      if (!user) {
        return null;
      }

      return `${user.firstName} ${user.lastName}`;
    },

    isLoadingHuntAreaInfo(huntArea) {
      if (!huntArea.id) {
        return false;
      }

      const isLoadingHuntAreaInfo = `${this.huntAreasCacheStorePath}/${IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID}`;
      return this.$store.getters[isLoadingHuntAreaInfo](huntArea.id);
    },

    huntAreaInfo(huntArea) {
      if (!huntArea.id) {
        return null;
      }

      const getHuntAreaInfo = `${this.huntAreasCacheStorePath}/${GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID}`;
      return this.$store.getters[getHuntAreaInfo](huntArea.id);
    },

    huntAreaInfoLoadFailed(huntArea) {
      if (!huntArea.id) {
        return null;
      }

      const getHuntAreaInfoLoadError = `${this.huntAreasCacheStorePath}/${GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID}`;
      if (this.$store.getters[getHuntAreaInfoLoadError](huntArea.id)) {
        return true;
      }

      return false;
    },

    hasBorderTextClass(huntArea) {
      const huntAreaInfo = this.huntAreaInfo(huntArea);
      if (!huntAreaInfo) {
        return {};
      }

      return {
        "has-text-danger": !huntAreaInfo.hasBorder,
        "has-text-success": huntAreaInfo.hasBorder
      };
    }
  },

  computed: {
    hasHuntAreas() {
      if (!this.huntAreas) {
        return false;
      }

      return this.huntAreas.length > 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.tag {
  width: 100%;
}

.table {
  .loader {
    margin-top: 5px;
  }
}
</style>
